import type { Rules } from './rules';
import { rulesFor } from './rules';
import { type Household, type AssetMix, horizonYears, baseAge } from './household';
import { project } from './project';
import { householdVolatility, mixReturn } from './investments';
import { UK_HISTORY, householdMix, mixHistoricalReturn, inflationIn } from './history';

export interface MonteCarloResult {
  runs: number;
  volatility: number;
  method: 'history' | 'normal';
  /** for history: the mix resampled and whether it was assumed (Balanced) */
  mixAssumed: boolean;
  /** share of runs with no shortfall to the horizon */
  successRate: number;
  /** wealth (pensions + savings + ISA) by age at the 10th, 50th and 90th percentiles; today's money when the household asks */
  wealth: { age: number; p10: number; p50: number; p90: number }[];
  /** ages at which pensions ran out, at the same percentiles (null when they did not in that share of runs) */
  exhaust: { p10: number | null; p50: number | null; p90: number | null };
  /** heirs receive at the horizon, percentiles */
  heirs: { p10: number; p50: number; p90: number };
  /** share of runs in which spending could not be met by each age (first shortfall at or before it) */
  brokeByAge: { age: number; share: number }[];
}

/** Deterministic small PRNG so results are reproducible for the same inputs. */
function mulberry32(seed: number) { let a = seed >>> 0; return () => { a = (a + 0x6D2B79F5) >>> 0; let t = a; t = Math.imul(t ^ (t >>> 15), t | 1); t ^= t + Math.imul(t ^ (t >>> 7), t | 61); return ((t ^ (t >>> 14)) >>> 0) / 4294967296; }; }
const normal = (rnd: () => number) => { const u = Math.max(1e-12, rnd()), v = rnd(); return Math.sqrt(-2 * Math.log(u)) * Math.cos(2 * Math.PI * v); };
const pct = (xs: number[], p: number): number => { const s = xs.slice().sort((a, b) => a - b); return s[Math.min(s.length - 1, Math.max(0, Math.floor(p * (s.length - 1))))] ?? 0; };

/** Random-return simulation: each year's investment growth moves by N(0, volatility) percentage points. */
export function monteCarlo(h: Household, rules: Rules = rulesFor(h), seed = 12345, mixOverride?: AssetMix): MonteCarloResult {
  const runs = h.monteCarlo.runs, vol = h.monteCarlo.volatility ?? householdVolatility(h, rules), yrs = horizonYears(h), a0 = baseAge(h);
  const rnd = mulberry32(seed);
  const wealthRuns: number[][] = [], exhaust: (number | null)[] = [], heirs: number[] = [], firstShort: (number | null)[] = [];
  let ok = 0;
  const method = h.monteCarlo.method, chosen = mixOverride ? { mix: mixOverride, assumed: false } : householdMix(h, rules), { mix, assumed } = chosen, expected = mixReturn(mix, rules);
  const years = UK_HISTORY.years.map(x => x.y);
  // history: block bootstrap (blocks of 1-8 years) of joint returns and inflation, centred on the mix's expected return
  const histMean = years.reduce((a, y) => a + (mixHistoricalReturn(mix, y) ?? 0), 0) / years.length;
  for (let k = 0; k < runs; k++) {
    const yearlyDelta = new Array<number>(yrs + 1).fill(0), yearlyInflation = new Array<number>(yrs + 1).fill(h.infl);
    if (method === 'history') {
      let y = 1;
      while (y <= yrs) {
        const len = 1 + Math.floor(rnd() * 8), start = Math.floor(rnd() * years.length);
        for (let j = 0; j < len && y <= yrs; j++, y++) {
          const yr = years[(start + j) % years.length]!;
          yearlyDelta[y] = (mixHistoricalReturn(mix, yr) ?? histMean) - histMean; // history's shape around the plan's expected return
          yearlyInflation[y] = Math.max(-3, Math.min(15, (inflationIn(yr) ?? h.infl) + (h.infl - 3.5))); // history's inflation shape, re-centred on the household's assumption (long-run UK CPI is ~3.5%)
        }
      }
    } else for (let y = 1; y <= yrs; y++) yearlyDelta[y] = normal(rnd) * vol;
    const r = project(h, yrs, rules, method === 'history' ? { yearlyDelta, yearlyInflation } : { yearlyDelta });
    if (r.shortfallYears === 0) ok++;
    wealthRuns.push(r.rows.map(x => (x.pots + x.cash + x.isa) / (h.todays ? x.f : 1)));
    exhaust.push(r.potExhaustAge); heirs.push(r.heirsReceive / r.D); firstShort.push(r.firstShortfallAge);
  }
  const wealth = Array.from({ length: yrs }, (_, j) => { const col = wealthRuns.map(w => w[j] ?? 0); return { age: a0 + j + 1, p10: pct(col, 0.1), p50: pct(col, 0.5), p90: pct(col, 0.9) }; });
  const ex = exhaust.filter((x): x is number => x !== null);
  const exPct = (p: number) => (ex.length / runs >= 1 - p ? pct(ex, p) : null);
  void expected;
  const brokeByAge = Array.from({ length: yrs }, (_, j) => { const age = a0 + j + 1; return { age, share: firstShort.filter(x => x !== null && x <= age).length / runs }; });
  return { runs, volatility: vol, method, mixAssumed: assumed, successRate: ok / runs, wealth, exhaust: { p10: exPct(0.1), p50: exPct(0.5), p90: exPct(0.9) }, heirs: { p10: pct(heirs, 0.1), p50: pct(heirs, 0.5), p90: pct(heirs, 0.9) }, brokeByAge };
}
